import React, { useMemo } from 'react';
import styles from './BodyRow.module.scss';
import DropdownSelect from '../../../../../components/dropdownSelect';
import { Form } from 'antd';
import {
  getOptionsSelect,
  getOptionsSelectTransports,
} from '../../lib/getOptionsSelect';
import useAccessPermitType from '../../api/useAccessPermitType';
import useClientsByAddressQuery from '../../api/useClientsByAddressQuery';
import useTransportForPermitQuery, {
  keyTransportsForPermit,
} from '../../api/useTransportForPermitQuery';
import usePermitStatusesQuery from '../../api/usePermitStatusesQuery';
import { getRenderControl } from '../../../../../components/SingleControlCustom/getRenderControl';
import { useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';
import { modalType, useModal } from '../../modalProvider';
import moment from 'moment';
import DispexInput, { typeInput } from '../../../../../components/DispexInput';
import { permitKeys, permitValuesForChanged } from '../../lib/permitKeys';
import { permitConstants } from '../../../../../constants/permit.constants';

const BodyRow = ({ form, isEdit, permit, editingAcccess, companyId }) => {
  const approveTransportPermit = useSelector(
    (store) => store.authentication.userInfo.ApproveTransportPermit
  );

  const queryClient = useQueryClient();

  const { open: openTransport } = useModal(modalType.TRANSPORT_FORM);

  const hasChangeStatus = isEdit;

  const clientIdValue = form.getFieldValue('clientId');
  const transportPermitTypeIdValue = form.getFieldValue(
    'transportPermitTypeId'
  );
  const addressIdValue = form.getFieldValue('addressId');
  const houseIdValue = form.getFieldValue('houseId');
  const streetIdValue = form.getFieldValue('streetId');
  const transportIdValue = form.getFieldValue('transportId');
  const statusIdValue = form.getFieldValue('permitStatusId');

  const { data: permitTypes, isFetching: isFetchingTypes } =
    useAccessPermitType();
  const { data: clients, isFetching: isFetchingClients } =
    useClientsByAddressQuery(isEdit ? permit?.addressId : addressIdValue, {
      enabled: isEdit ? true : !!addressIdValue,
    });
  const { data: transports, isFetching: isFetchingTransports } =
    useTransportForPermitQuery(
      {
        clientId: isEdit ? permit?.clientId : clientIdValue,
        houseId: isEdit ? permit?.houseId : houseIdValue,
      },
      {
        enabled: isEdit
          ? true
          : !!clientIdValue && !!addressIdValue && !!houseIdValue,
      }
    );

  const { data: permitStatus = [], isFetching: isFetchingStatus } =
    usePermitStatusesQuery({ enabled: hasChangeStatus });

  const optionsClient = useMemo(
    () => getOptionsSelect({ data: clients, isClient: true }),
    [clients]
  );
  const optionsPermitTypes = useMemo(
    () => getOptionsSelect({ data: permitTypes }),
    [permitTypes]
  );
  const optionsTransports = useMemo(
    () => getOptionsSelectTransports(transports, isEdit),
    [transports, isEdit]
  );
  const optionsPermitStatus = useMemo(
    () => getOptionsSelect({ data: permitStatus }),
    [permitStatus]
  );

  const filteredOptionsStatus = useMemo(() => {
    if (statusIdValue === permitKeys.UNDER_APPROVAL_PERMIT) {
      return optionsPermitStatus.filter((item) =>
        permitValuesForChanged[permitKeys.UNDER_APPROVAL_PERMIT].includes(
          item.id
        )
      );
    }

    return optionsPermitStatus;
  }, [optionsPermitStatus, statusIdValue]);

  const handleChangeClient = () => {
    form.setFieldsValue({
      transportId: null,
    });
  };

  const handleChangeTransportPermitType = (value) => {
    if (value === permitConstants.ONE_TIME_PASS) {
      form.setFieldsValue({
        startDate: moment(),
        expireDate: moment().add(4, 'hours'),
      });

      return;
    }

    form.setFieldsValue({
      expireDate: null,
    });
  };

  const handleOpenAddTransport = () => {
    const onSuccess = (transportId) => {
      queryClient.refetchQueries(
        keyTransportsForPermit({
          clientId: clientIdValue,
          houseId: houseIdValue,
        })
      );

      form.setFieldsValue({
        transportId: transportId || null,
      });
    };

    const valueForTs = {
      clientId: clientIdValue,
      houseId: houseIdValue,
      streetId: streetIdValue,
      addressId: addressIdValue,
    };

    openTransport({
      companyId: Number(companyId),
      onSuccess,
      valueForTs,
      addFromPermit: true,
    });
  };

  const hasRequiredComment = () => {
    return Boolean(
      statusIdValue !== permit?.permitStatus?.id &&
        statusIdValue === permitKeys.PROHIBITED_PERMIT
    );
  };

  return (
    <>
      <div className={styles.rowTwice}>
        <Form.Item label="Клиент" style={{ margin: 0, flex: 1 }}>
          {form.getFieldDecorator('clientId', {
            initialValue: isEdit && permit?.clientId,
            rules: [
              {
                required: true,
                message: 'Пожалуйста, укажите клиента',
              },
            ],
          })(
            <DropdownSelect
              onChange={handleChangeClient}
              multiple={false}
              options={optionsClient}
              renderControl={getRenderControl({
                options: optionsClient,
                isClear: false,
                value: clientIdValue,
                placeholder: 'Выберите из списка',
                disabled: isFetchingClients || !addressIdValue || isEdit,
                loading: isFetchingClients,
              })}
            />
          )}
        </Form.Item>
        <Form.Item label="Тип пропуска" style={{ margin: 0, flex: 1 }}>
          {form.getFieldDecorator('transportPermitTypeId', {
            initialValue: isEdit && permit?.permitType?.id,
            rules: [
              {
                required: true,
                message: 'Пожалуйста, укажите тип пропуска',
              },
            ],
          })(
            <DropdownSelect
              onChange={handleChangeTransportPermitType}
              multiple={false}
              options={optionsPermitTypes}
              renderControl={getRenderControl({
                options: optionsPermitTypes,
                isClear: false,
                value: transportPermitTypeIdValue,
                placeholder: 'Выберите из списка',
                disabled: isFetchingTypes || isEdit,
                loading: isFetchingTypes,
              })}
            />
          )}
        </Form.Item>
      </div>
      <div className={styles.rowTwice}>
        <Form.Item label="Транспортное средство" style={{ margin: 0, flex: 1 }}>
          {form.getFieldDecorator('transportId', {
            initialValue: isEdit && permit?.transportId,
            rules: [
              {
                required: true,
                message: 'Пожалуйста, укажите транспортное средство',
              },
            ],
          })(
            <DropdownSelect
              multiple={false}
              options={optionsTransports}
              renderControl={getRenderControl({
                options: optionsTransports,
                isClear: false,
                value: transportIdValue,
                placeholder: 'Выберите ТС из списка',
                disabled:
                  isFetchingTransports ||
                  !clientIdValue ||
                  !editingAcccess ||
                  isEdit,
                loading: isFetchingTransports,
              })}
              renderActions={({ onCancel }) => {
                return (
                  <div
                    onClick={() => {
                      handleOpenAddTransport();
                      onCancel();
                    }}
                    className={styles.btnTs}
                  >
                    Добавить новое ТС
                  </div>
                );
              }}
            />
          )}
        </Form.Item>
        {hasChangeStatus && (
          <Form.Item label="Статус" style={{ margin: 0, flex: 1 }}>
            {form.getFieldDecorator('permitStatusId', {
              initialValue: isEdit && permit?.permitStatus?.id,
              rules: [
                {
                  required: true,
                  message: 'Пожалуйста, укажите статус пропуска',
                },
              ],
            })(
              <DropdownSelect
                multiple={false}
                options={filteredOptionsStatus}
                renderControl={getRenderControl({
                  options: filteredOptionsStatus,
                  isClear: false,
                  value: statusIdValue,
                  placeholder: 'Выберите статус из списка',
                  disabled: isFetchingStatus || !approveTransportPermit,
                  loading: isFetchingStatus,
                })}
              />
            )}
          </Form.Item>
        )}
      </div>
      <div className={styles.rowTwice}>
        {hasChangeStatus && (
          <Form.Item label="Комментарий" style={{ margin: 0, flex: 1 }}>
            {form.getFieldDecorator('comment', {
              rules: [
                {
                  required: hasRequiredComment(),
                  message: 'Пожалуйста, укажите комментарий',
                },
              ],
            })(
              <DispexInput
                placeholder="Комментарий"
                typeInput={typeInput.textarea}
                maxLength={1024}
                autoSize={{ minRows: 2, maxRows: 4 }}
              />
            )}
          </Form.Item>
        )}
      </div>
    </>
  );
};

export default BodyRow;
